import { FC, MutableRefObject, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Bookmark, BookmarkNotes } from '@bonusly/common-bookmark';
import { FeedbackEvent } from '@bonusly/common-feedback';
import {
	AspectRatio,
	Box,
	Center,
	Display,
	Flex,
	Image,
	rem,
	Spacer,
	Stack,
	Text,
	useBoolean,
	useBreakpointValue,
	useColorModeValue,
	useDisclosure,
	UseDisclosureReturn,
	VStack,
} from '@bonusly/design-system';
import { useDispatch, useEventListener } from '@bonusly/events';
import { Recipient } from '@bonusly/recipient-entities';
import { Permission, useCurrentCompany, useFeatureFlag, usePermission } from '@bonusly/who-am-i';

import { CelebrationType } from '../../../../../schemas/celebrations';
import { Bonus as BonusType } from '../../../../../types/bonus';
import { getBonusType } from '../../../../../utils/bonus';
import { getCelebrationStyles } from '../../../../celebrations/utils';
import { Reveal } from '../../../../display/reveal';
import { AvatarBubbleWrap } from '../../../../post-wrap/avatar-bubble-wrap';
import { CustomTileWrap } from '../../../../post-wrap/custom-tile-wrap';
import { getCollectibleImageUrl } from '../../../../post-wrap/post-wrap-images';
import { AwardHeader } from '../../components/bonus/award-header';
import { BonusCardActions } from '../../components/bonus/bonus-card-actions';
import { BonusHeader } from '../../components/bonus/bonus-header';
import { BonusText } from '../../components/bonus/bonus-text';
import { GiverName } from '../../components/bonus/giver-name';
import { PrivacyBadge } from '../../components/bonus/privacy-badge';
import { Comments } from '../../components/comments';
import { ImageList } from '../../components/common/image';
import { ReactionDetails } from '../../components/reactions/reaction-details';
import { CommentEvent } from '../../events';
import { useBonusAnalytics } from '../../hooks/use-bonus-analytics';
import { useContextualizedBonusDetails } from '../../hooks/use-contextualized-bonus-details';
import { getCelebrationType, getRecognitionType, parseContent } from '../../utils/bonus';
import { AudioBonusAttachment } from '../audio/audio-attachment';
import { EmployeeSpotlightAttachment } from '../employee-spotlight';
import { EmployeeSpotlightButton } from '../employee-spotlight/employee-spotlight-button';
import { LinkPreviewAttachment } from '../link-preview';
import { UserAvatarGroup } from '../user-avatar/user-avatar-group';
import { VideoAttachment } from '../video/video-attachment';
import { CelebrationHeader } from './celebration-header';
import { FeedbackPromptPanel } from './feedback-prompt-panel';
import { WorkAnniversarySnapshotCTA } from './work-anniversary-snapshot-cta';

export type DefaultUser = {
	id: string;
	imageUrl: string;
	name: string;
	username: string;
};

export type BonusContentProps = {
	bonus: BonusType;
	feedFilter?: string;
	handleClickDelete?: VoidFunction;
	editBonusModal?: UseDisclosureReturn;
	shouldCommentBeOpenedInModal?: boolean;
	commentBoxRef?: MutableRefObject<HTMLDivElement | null>;
};

export const BonusContent: FC<BonusContentProps> = ({
	bonus: baseBonus,
	feedFilter,
	handleClickDelete,
	editBonusModal,
	commentBoxRef,
	shouldCommentBeOpenedInModal = false,
}) => {
	let bonus = baseBonus;
	const company = useCurrentCompany();
	const canAccessSpotlight = usePermission(Permission.CanAccessSpotlight);
	const { track } = useAnalytics();
	const dispatch = useDispatch();
	const commentEditor = useDisclosure();
	const [trackedCommentStart, setTrackedCommentStart] = useBoolean();
	const { isUserReceiver } = useContextualizedBonusDetails(bonus);
	const { images, text } = useMemo(() => parseContent(bonus), [bonus]);
	const { trackCommentStart, trackCommentSubmitFailure } = useBonusAnalytics();
	const [initalText, setInitialText] = useState<string>();
	const navigate = useNavigate();

	const onClose = () => {
		commentEditor.onClose();
		setTrackedCommentStart.off();
	};

	useEventListener(CommentEvent.Open, ({ id }: { id: string }) => {
		if (id !== bonus.id) {
			commentEditor.onClose();
		}
	});

	const handleOpenInput = (text = '') => {
		dispatch(CommentEvent.Open, { id: bonus.id });

		if (!trackedCommentStart) {
			trackCommentStart({
				celebrationType: getCelebrationType(bonus),
				isThankYou: isUserReceiver,
				recognitionType: getRecognitionType(bonus),
			});
			setTrackedCommentStart.on();
		}

		dispatch('init-comment', { bonus, text });
		setInitialText(text);
		commentEditor.onOpen();
	};

	const onOpenFromAction = () => {
		dispatch(FeedbackEvent.OpenRequest, { reference: bonus });
		track(AnalyticsEvent.Feedback.RequestFromRecognition, { source: 'action' });
	};

	const onOpenFromCTA = () => {
		dispatch(FeedbackEvent.OpenRequest, { reference: bonus });
		track(AnalyticsEvent.Feedback.RequestFromRecognition, { source: 'cta' });
	};

	const giveFeedback = (person: DefaultUser) => {
		dispatch(FeedbackEvent.OpenUnsolicited, {
			defaultUsers: [{ ...person }],
			reference: bonus,
		});
		track(AnalyticsEvent.Feedback.GiveFromRecognition, { source: 'cta' });
	};

	// recipients based on reason tokens which support groups
	if (bonus.reason_tokens) {
		const recipients: Recipient[] = [];
		bonus.reason_tokens.forEach((token) => {
			const recipient = Recipient.fromReasonToken(token, bonus.receivers);
			if (recipient) recipients.push(recipient);
		});
		bonus = { ...bonus, recipients };
	}
	const isVideoBonus = bonus.attachments.length > 0 && bonus.attachments[0].category === 'video';
	const isAudioBonus = bonus.attachments.length > 0 && bonus.attachments[0].category === 'audio';
	const disableVideo = company.disable_video_uploads && company.disable_video_recordings;
	const includeVideo = !disableVideo && isVideoBonus;
	const isAudioFeatureEnabled = useFeatureFlag('audio-attachment');
	// Audio playback should always work when feature flag is enabled, even if recording is disabled
	// (existing audio attachments should still be viewable)
	const includeAudio = isAudioFeatureEnabled && isAudioBonus;

	const HEADER_TYPES = {
		celebration: CelebrationHeader,
		award: AwardHeader,
		bonus: BonusHeader,
	};
	// default type is bonus, but just in case, we have a fallback to BonusHeader
	const BonusContentHeader = HEADER_TYPES[getBonusType(bonus)] || BonusHeader;

	return (
		<Bookmark referenceId={bonus.id} referenceType="bonus" referenceText={bonus.reason} bookmark={bonus.bookmark}>
			<Box p={2}>
				<Stack
					data-testid="bonus-card"
					p={3}
					spacing={4}
					cursor="pointer"
					onClick={(e: React.MouseEvent<HTMLElement>) => {
						const target = e.target as HTMLElement;
						if (
							target.closest('button') ||
							target.closest('a') ||
							target.closest('[role="button"]') ||
							target.closest('video') ||
							target.closest('.video-controls') ||
							target.closest('audio') ||
							target.closest('.audio-player') ||
							target.closest('[role="slider"]') ||
							target.closest('[data-behavior="bonus-text"]')
						) {
							return;
						}
						const search = window.location.search;
						navigate(`/bonuses/${bonus.id}${search}`);
					}}
				>
					<BonusContentHeader bonus={bonus} onClickDelete={handleClickDelete} onClickEdit={editBonusModal?.onOpen} />

					<Stack spacing={3}>
						<BonusContentBody bonus={bonus} text={text} images={images} />
						{includeVideo && <VideoAttachment bonus={bonus} focused={shouldCommentBeOpenedInModal} />}
						{includeAudio && <AudioBonusAttachment bonus={bonus} />}
						{!bonus.visual_state?.gift_wrapped && <ImageList images={images} />}
						<BookmarkNotes />
						<PrivacyBadge bonus={bonus} />
					</Stack>

					<ReactionDetails bonus={bonus} />

					<BonusCardActions
						bonus={bonus}
						onClickAddOn={() => handleOpenInput('+')}
						onClickComment={() => handleOpenInput('')}
						onClickRequestFeedback={onOpenFromAction}
					/>

					{canAccessSpotlight && <WorkAnniversarySnapshotCTA bonus={bonus} />}

					<FeedbackPromptPanel bonus={bonus} giveFeedback={giveFeedback} requestFeedback={onOpenFromCTA} />
				</Stack>

				<Comments
					bonus={bonus}
					feedFilter={feedFilter}
					initalText={initalText}
					isModalDisplay={shouldCommentBeOpenedInModal}
					isOpen={commentEditor.isOpen}
					onClose={onClose}
					onCommentSubmitFailure={(reason: string) => trackCommentSubmitFailure({ reason })}
					onOpen={() => handleOpenInput('')}
					commentBoxRef={commentBoxRef}
				/>
			</Box>
		</Bookmark>
	);
};

type BonusContentBodyProps = {
	bonus: BonusType;
	text: string;
	images: ReturnType<typeof parseContent>['images'];
};

const BonusContentBody: FC<BonusContentBodyProps> = ({ bonus, text, images }) => {
	const ratio = useBreakpointValue({ base: 4 / 3, xs: 1 });
	const max = useBreakpointValue({ base: 1, sm: 3 });
	const canAccessSpotlight = usePermission(Permission.CanAccessSpotlight);
	const wrapperBgColor = useColorModeValue('whiteAlpha.700', 'blackAlpha.700');
	const isEmployeeSpotlightBonus = bonus.attachments.length > 0 && bonus.attachments[0].category === 'employee_spotlight';
	const isLinkPreviewBonus = bonus.attachments.length > 0 && bonus.attachments[0].category === 'link_preview';

	if (getBonusType(bonus) === 'celebration') {
		const { giver, image_url } = bonus;
		const { bgColor, imageComponent } = getCelebrationStyles(bonus.award_type as unknown as CelebrationType, canAccessSpotlight);

		if (canAccessSpotlight) {
			return (
				<>
					<Box position="relative" minH={rem(230)} bgColor={bgColor} overflow="hidden" mx={-5}>
						{image_url && <Image src={image_url} objectFit="cover" position="absolute" inset={0} w="full" h="full" />}
						{!image_url && imageComponent && (
							<Center position="absolute" inset={0} w="full" h="full">
								{imageComponent}
							</Center>
						)}
						<Box m={rem(36)} borderRadius="xl" backdropFilter="blur(18.5px)" border="1.5px solid" borderColor="bg.surface" bgColor={wrapperBgColor}>
							<VStack
								spacing={3}
								px={4}
								py={8}
								position="relative"
								minH={rem(230)}
								justify="center"
								alignContent="center"
								bgColor="bg.surface"
								m={4}
								borderRadius="lg"
								boxShadow="0px 0px 10.85px 0px var(--chakra-colors-success-bg)"
							>
								<UserAvatarGroup max={max} size="2xl" users={bonus.receivers} recipients={bonus.recipients} />
								<Text fontFamily="display" fontSize={200} fontWeight="extrabold" textAlign="center">
									{giver.display_name}
								</Text>
								<BonusText cursor="text" bonus={bonus} text={text} textAlign="center" />
								{isEmployeeSpotlightBonus && (
									<EmployeeSpotlightButton
										attachment={bonus.attachments[0]}
										bonus={bonus}
										receiverFirstName={(bonus.receivers[0].preferred_first_name || bonus.receivers[0].first_name) ?? ''}
									/>
								)}
							</VStack>
						</Box>
					</Box>
				</>
			);
		}

		return (
			<Flex bgColor={image_url ? 'bg.surface' : bgColor} flexDir={{ base: 'column-reverse', xs: 'row' }} mx={-5} px={5}>
				<Stack justify="center" py={4}>
					<UserAvatarGroup max={max} mr={rem(64 / 2.75)} size="xl" users={bonus.receivers} recipients={bonus.recipients} />
					<Display mb={2} size="2xs">
						{giver.display_name}
					</Display>
					<BonusText cursor="text" bonus={bonus} text={text} />
				</Stack>

				<Spacer />

				{image_url && (
					<AspectRatio w="full" maxW={{ sm: '40%' }} ratio={ratio}>
						<Image src={image_url} objectFit="cover" w="full" h="full" />
					</AspectRatio>
				)}

				{!image_url && imageComponent && <Center>{imageComponent}</Center>}
			</Flex>
		);
	}

	const giftWrapStyle = bonus.visual_state?.gift_wrap_style ?? '';
	const giftWrapColor = bonus.visual_state?.gift_wrap_color ?? '';
	const decorationImageUrl = bonus.visual_state?.gift_wrapped ? getCollectibleImageUrl(giftWrapStyle, giftWrapColor, 1) : undefined;
	const isAvatarWrap = bonus.visual_state?.gift_wrapped && giftWrapStyle === 'avatar';
	const isCustomWrap = bonus.visual_state?.gift_wrapped && giftWrapStyle === 'custom';
	const customImageUrl = bonus.visual_state?.custom_image_url;

	if (isAvatarWrap) {
		const avatarRecipients = bonus.receivers.map((r) => ({ id: r.id, src: r.profile_pic_url, name: r.display_name }));
		return (
			<Box position="relative" mx={-5} overflow="hidden">
				<Box position="absolute" top={0} left={0} right={0} bottom={0}>
					<AvatarBubbleWrap recipients={avatarRecipients} seed={bonus.id} />
				</Box>
				<Box p={9} position="relative">
					<Box
						backdropFilter="blur(4px)"
						border="1.5px solid white"
						borderRadius="32px"
						bg="rgba(255,255,255,0.25)"
						boxShadow="0px 5px 30px 0px rgba(255,255,255,0.45)"
						p={4}
					>
						<Box bg="bg.surface" borderRadius="20px" p={4}>
							<BonusText cursor="text" bonus={bonus} prefix={<GiverName bonus={bonus} />} text={text} />
							<ImageList images={images} pt={2} />
							<Reveal if={isEmployeeSpotlightBonus}>
								<Box pt={2} borderRadius="2xl" overflow="hidden">
									<EmployeeSpotlightAttachment attachment={bonus.attachments[0]} bonus={bonus} />
								</Box>
							</Reveal>
							<Reveal if={isLinkPreviewBonus}>
								<Box pt={2}>
									<LinkPreviewAttachment attachment={bonus.attachments[0]} />
								</Box>
							</Reveal>
						</Box>
					</Box>
				</Box>
			</Box>
		);
	}

	if (isCustomWrap) {
		return (
			<Box position="relative" mx={-5} overflow="hidden">
				<Box position="absolute" top={0} left={0} right={0} bottom={0}>
					<CustomTileWrap src={customImageUrl} />
				</Box>
				<Box p={9} position="relative">
					<Box
						backdropFilter="blur(4px)"
						border="1.5px solid white"
						borderRadius="32px"
						bg="rgba(255,255,255,0.25)"
						boxShadow="0px 5px 30px 0px rgba(255,255,255,0.45)"
						p={4}
					>
						<Box bg="bg.surface" borderRadius="20px" p={4}>
							<BonusText cursor="text" bonus={bonus} prefix={<GiverName bonus={bonus} />} text={text} />
							<ImageList images={images} pt={2} />
							<Reveal if={isEmployeeSpotlightBonus}>
								<Box pt={2} borderRadius="2xl" overflow="hidden">
									<EmployeeSpotlightAttachment attachment={bonus.attachments[0]} bonus={bonus} />
								</Box>
							</Reveal>
							<Reveal if={isLinkPreviewBonus}>
								<Box pt={2}>
									<LinkPreviewAttachment attachment={bonus.attachments[0]} />
								</Box>
							</Reveal>
						</Box>
					</Box>
				</Box>
			</Box>
		);
	}

	if (decorationImageUrl) {
		return (
			<Box position="relative" mx={-5} overflow="hidden" backgroundImage={`url(${decorationImageUrl})`} backgroundRepeat="repeat-y" backgroundSize="100% auto">
				<Box p={9}>
					<Box
						backdropFilter="blur(4px)"
						border="1.5px solid white"
						borderRadius="32px"
						bg="rgba(255,255,255,0.25)"
						boxShadow="0px 5px 30px 0px rgba(255,255,255,0.45)"
						p={4}
					>
						<Box bg="bg.surface" borderRadius="20px" p={4}>
							<BonusText cursor="text" bonus={bonus} prefix={<GiverName bonus={bonus} />} text={text} />
							<ImageList images={images} pt={2} />
							<Reveal if={isEmployeeSpotlightBonus}>
								<Box pt={2} borderRadius="2xl" overflow="hidden">
									<EmployeeSpotlightAttachment attachment={bonus.attachments[0]} bonus={bonus} />
								</Box>
							</Reveal>
							<Reveal if={isLinkPreviewBonus}>
								<Box pt={2}>
									<LinkPreviewAttachment attachment={bonus.attachments[0]} />
								</Box>
							</Reveal>
						</Box>
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		<Box>
			<BonusText cursor="text" bonus={bonus} prefix={<GiverName bonus={bonus} />} text={text} />
			<Reveal if={isEmployeeSpotlightBonus}>
				<Box pt={2}>
					<EmployeeSpotlightAttachment attachment={bonus.attachments[0]} bonus={bonus} />
				</Box>
			</Reveal>
			<Reveal if={isLinkPreviewBonus}>
				<Box pt={2}>
					<LinkPreviewAttachment attachment={bonus.attachments[0]} />
				</Box>
			</Reveal>
		</Box>
	);
};
